import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { ChoiceChip, Label, type StatusIndicatorState } from '@treams/treams-ui';

import {
	RETENTION_RISKS,
	type RetentionRiskLevel,
	retentionRiskDescriptionKey,
	retentionRiskLabelKey,
} from '../retentionRisk';
import { TalentReviewTooltip } from './TalentReviewTooltip';

const STATUS: Record<RetentionRiskLevel, StatusIndicatorState> = {
	LOW: 'success',
	MEDIUM: 'warning',
	HIGH: 'error',
};

/**
 * Optional retention risk. Always shown; choosing the selected option again clears it.
 * @param props component props
 * @param props.value the stored risk, if any
 * @param props.readOnly past days and submitted reviews cannot change it
 * @param props.onChange fires with the new risk, or null when cleared
 */
export default function TalentReviewRetentionRiskField(props: {
	value?: RetentionRiskLevel | null;
	readOnly?: boolean;
	onChange: (value: RetentionRiskLevel | null) => void;
}) {
	const { t, i18n } = useTranslation();
	const label = t('talentReviews.v2.sidebar.card.retentionRiskLabel');

	return (
		<>
			<Label>{label}</Label>
			<Box mt={1}>
				<ChoiceChip.Group
					label={label}
					value={props.value}
					disabled={props.readOnly}
					clearable
					onChange={(next) => {
						if (next === 'LOW' || next === 'MEDIUM' || next === 'HIGH') {
							props.onChange(next);
							return;
						}

						props.onChange(null);
					}}
				>
					{RETENTION_RISKS.map((risk) => {
						const descriptionKey = retentionRiskDescriptionKey(risk);
						const chip = (
							<ChoiceChip
								value={risk}
								status={STATUS[risk]}
								label={t(retentionRiskLabelKey(risk))}
							/>
						);

						if (!i18n.exists(descriptionKey)) {
							return <Fragment key={risk}>{chip}</Fragment>;
						}

						return (
							<TalentReviewTooltip key={risk} content={t(descriptionKey)} position="top" portal>
								{chip}
							</TalentReviewTooltip>
						);
					})}
				</ChoiceChip.Group>
			</Box>
		</>
	);
}
